import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { formatCellValue } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DollarSign,
  Users,
  Building2,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Save,
  AlertTriangle,
  Info,
  Clock,
  Zap,
  Settings,
  FileText,
  FileQuestion,
  Tags,
  Plug,
  Crown,
  BookOpen,
  MessageSquare,
  Trophy,
  Link2,
  Calendar,
  LayoutGrid,
  List,
  Download,
  Mail,
  Bell,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { PageShell } from '@/components/PageShell';
import { PageSkeleton } from '@/components/PageSkeleton';
import { Skeleton } from '@/components/ui/skeleton';

const LIMIT_FIELDS = [
  { key: 'employee_limit', label: 'Employee seats', unit: 'seats', icon: Building2, type: 'number' },
  { key: 'content_team_limit', label: 'Content team seats', unit: 'seats', icon: Users, type: 'number' },
  { key: 'document_limit', label: 'Documents', unit: 'documents', icon: FileText, type: 'number' },
  { key: 'max_chunks_per_document', label: 'Pages per document', unit: 'pages', icon: FileText, type: 'number' },
  { key: 'topics_limit', label: 'Topics', unit: 'topics', icon: Tags, type: 'number' },
  { key: 'content_gap_limit', label: 'Content gaps', unit: 'gaps', icon: FileQuestion, type: 'number' },
  { key: 'integrations_limit', label: 'Connectors', unit: 'connectors', icon: Plug, type: 'number' },
  { key: 'monthly_export_limit', label: 'Monthly exports', unit: 'exports', icon: Download, type: 'number' },
  { key: 'scheduled_reports_limit', label: 'Scheduled reports', unit: 'reports', icon: Mail, type: 'number' },
  { key: 'notification_limit', label: 'Notifications', unit: 'notifications', icon: Bell, type: 'number' },
  { key: 'monthly_token_limit', label: 'Total monthly credit pool', unit: 'credits', icon: Zap, type: 'number' },
  { key: 'conversation_monthly_token_limit', label: 'Monthly Eva People credits', unit: 'credits', icon: Users, type: 'number' },
  { key: 'kb_monthly_token_limit', label: 'Monthly Eva Insights credits', unit: 'credits', icon: FileText, type: 'number' },
  { key: 'monthly_cost_limit_usd', label: 'Monthly cost', unit: 'USD', icon: DollarSign, type: 'decimal' },
  { key: 'tokens_per_seat_month', label: 'Eva People credits per seat', unit: 'credits/seat', icon: Crown, type: 'number' },
  { key: 'user_daily_capacity_pct', label: 'Daily Eva People rate limit', unit: '%', icon: Clock, type: 'decimal' },
  { key: 'user_weekly_capacity_pct', label: 'Weekly Eva People rate limit', unit: '%', icon: Calendar, type: 'decimal' },
  { key: 'user_monthly_capacity_pct', label: 'Monthly Eva People rate limit', unit: '%', icon: Settings, type: 'decimal' },
];

const LIMIT_FIELD_MAP = Object.fromEntries(LIMIT_FIELDS.map((field) => [field.key, field]));
const TOKEN_LIMIT_KEYS = new Set([
  'monthly_token_limit',
  'conversation_monthly_token_limit',
  'kb_monthly_token_limit',
  'tokens_per_seat_month',
]);
const CALCULATED_LIMIT_KEYS = new Set([
  'conversation_monthly_token_limit',
  'monthly_token_limit',
]);
// Per-user capacity guardrails are system-managed for trials: the backend
// (services/trial_config.normalize_trial_config) always re-derives them from the
// trial seat count on every save and read, so a value typed here would be
// silently discarded (EIP-223). Present them as read-only derived values instead.
// Keep in sync with backend/services/billing_plan_service.user_capacity_guardrails_for_seats.
const USER_CAPACITY_KEYS = new Set([
  'user_daily_capacity_pct',
  'user_weekly_capacity_pct',
  'user_monthly_capacity_pct',
]);
const END_USER_TOKEN_POOL_PCT = 70;
// Must match USER_FAIRUSE_BURST_DAYS in services/billing_plan_service.py. This
// read 30 (calendar days) against the backend's 6, so saving a tier here wrote
// per-user guardrails five times tighter than the backend derives, throttling
// real users well below their plan.
const GUARDRAIL_BURST_DAYS = 6;

const isUserCapacityField = (field) => USER_CAPACITY_KEYS.has(field?.key);

const calculateUserCapacityGuardrails = (values = {}) => {
  const seats = Math.max(Math.trunc(Number(values.employee_limit) || 1), 1);
  const round4 = (n) => Math.round(n * 10000) / 10000;
  const monthlyPct = END_USER_TOKEN_POOL_PCT / seats;
  return {
    user_daily_capacity_pct: round4(END_USER_TOKEN_POOL_PCT / (GUARDRAIL_BURST_DAYS * seats)),
    // Clamped to the monthly ceiling, mirroring the backend: with a burst
    // denominator below 7, the raw weekly figure would otherwise exceed the
    // hard monthly fair-use cap.
    user_weekly_capacity_pct: round4(
      Math.min((END_USER_TOKEN_POOL_PCT * 7) / (GUARDRAIL_BURST_DAYS * seats), monthlyPct)
    ),
    user_monthly_capacity_pct: round4(monthlyPct),
  };
};

const LIMIT_SECTIONS = [
  {
    key: 'knowledge-base',
    label: 'Knowledge Base',
    icon: BookOpen,
    fields: ['document_limit', 'max_chunks_per_document', 'topics_limit', 'content_gap_limit', 'kb_monthly_token_limit'],
  },
  {
    key: 'people',
    label: 'People',
    icon: Users,
    fields: ['employee_limit', 'content_team_limit'],
  },
  {
    key: 'conversations',
    label: 'Conversations',
    icon: MessageSquare,
    fields: [
      'user_daily_capacity_pct',
      'user_weekly_capacity_pct',
      'user_monthly_capacity_pct',
      'tokens_per_seat_month',
      'conversation_monthly_token_limit',
      'monthly_token_limit',
      'monthly_cost_limit_usd',
    ],
  },
  {
    key: 'engagement',
    label: 'Engagement',
    icon: Trophy,
    fields: ['monthly_export_limit', 'scheduled_reports_limit', 'notification_limit'],
  },
  {
    key: 'connectors',
    label: 'Connectors',
    icon: Link2,
    fields: ['integrations_limit'],
  },
];

const getLimitFieldsForSection = (section) => section.fields
  .map((key) => LIMIT_FIELD_MAP[key])
  .filter(Boolean);

const SectionLabel = ({ section, compact = false }) => {
  const Icon = section.icon || Settings;
  return (
    <span className={`inline-flex items-center gap-2 ${compact ? '' : 'font-medium'}`}>
      <Icon className="h-4 w-4 text-muted-foreground" />
      <span>{section.label}</span>
    </span>
  );
};

const buildTrialConfigPayload = (config) => ({
  ...config,
  max_documents: config.document_limit ?? config.max_documents,
  max_users: config.content_team_limit ?? config.max_users,
});

const formatNumber = (value) => Number(value || 0).toLocaleString();

const formatCompactNumber = (value) => {
  const numeric = Number(value || 0);
  const abs = Math.abs(numeric);
  const compact = (divisor, suffix) => {
    const scaled = Math.trunc((numeric / divisor) * 10) / 10;
    return `${scaled.toLocaleString(undefined, { maximumFractionDigits: 1 })}${suffix}`;
  };
  if (abs >= 1000000000) {
    return compact(1000000000, 'B');
  }
  if (abs >= 1000000) {
    return compact(1000000, 'M');
  }
  if (abs >= 1000) {
    return compact(1000, 'K');
  }
  return formatNumber(numeric);
};

const formatCurrency = (value) => {
  const numeric = Number(value || 0);
  return `$${numeric.toLocaleString(undefined, {
    minimumFractionDigits: Number.isInteger(numeric) ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatPercentLimit = (value) => {
  const numeric = Number(value || 0);
  return `${numeric.toLocaleString(undefined, {
    maximumFractionDigits: numeric < 0.01 ? 4 : numeric < 1 ? 2 : 1,
  })}%`;
};

const formatLimitValue = (value, field) => {
  if (value === null || value === undefined || value === '') return 'Not configured';
  if (value === -1) return 'Unlimited';
  if (field?.unit === 'USD') return formatCurrency(value);
  if (field?.unit === '%') return formatPercentLimit(value);
  if (field?.key === 'monthly_token_limit') return formatCompactNumber(value);
  return formatNumber(value);
};

const toUserTokens = (value, scale) => {
  if (value === null || value === undefined || value === '') return null;
  if (Number(value) === -1) return -1;
  const numeric = Number(value || 0);
  if (numeric <= 0) return 0;
  return Math.max(Math.ceil(numeric / Math.max(Number(scale || 1), 1)), 1);
};

const isTokenLimitField = (field) => TOKEN_LIMIT_KEYS.has(field?.key);
const isCalculatedLimitField = (field) => CALCULATED_LIMIT_KEYS.has(field?.key);

const calculateEvaPeopleTokenLimit = (values = {}) => {
  const seats = Number(values.employee_limit || 0);
  const tokensPerSeat = Number(values.tokens_per_seat_month || 0);
  if (seats === -1 || tokensPerSeat === -1) return -1;
  return Math.max(seats, 0) * Math.max(tokensPerSeat, 0);
};

const calculateTotalTokenPool = (values = {}) => {
  const evaPeopleTokens = calculateEvaPeopleTokenLimit(values);
  const evaInsightsTokens = Number(values.kb_monthly_token_limit || 0);
  if (evaPeopleTokens === -1 || evaInsightsTokens === -1) return -1;
  return Math.max(evaPeopleTokens, 0) + Math.max(evaInsightsTokens, 0);
};

const getCalculatedLimitValue = (field, values = {}) => {
  if (field?.key === 'conversation_monthly_token_limit') return calculateEvaPeopleTokenLimit(values);
  if (field?.key === 'monthly_token_limit') return calculateTotalTokenPool(values);
  return values?.[field?.key];
};

const getLimitValue = (field, values = {}) => (
  isCalculatedLimitField(field) ? getCalculatedLimitValue(field, values) : values?.[field?.key]
);

const withCalculatedLimitValues = (values = {}) => ({
  ...values,
  conversation_monthly_token_limit: calculateEvaPeopleTokenLimit(values),
  monthly_token_limit: calculateTotalTokenPool(values),
});

// Trial limits the superadmin cannot edit: the calculated token pools stay
// derived from seats/per-seat inputs. Per-user capacity guardrails are editable
// (EIP-229) — an operator-set value persists via the user_capacity_pct_overridden
// sentinel; otherwise they auto-scale to the seat basis.
const isReadOnlyTrialField = (field) => isCalculatedLimitField(field);

const getTrialReadonlyValue = (field, values = {}) => getLimitValue(field, values);

function ViewToggle({ value, onChange, cardsLabel = 'Card view', tableLabel = 'Table view' }) {
  return (
    <div className="inline-flex rounded-lg bg-muted p-1" aria-label="Layout view">
      <Button
        type="button"
        variant={value === 'cards' ? 'secondary' : 'ghost'}
        size="sm"
        className={value === 'cards' ? 'px-3 shadow-sm' : 'px-3 text-muted-foreground'}
        onClick={() => onChange('cards')}
        aria-label={cardsLabel}
        title={cardsLabel}
      >
        <LayoutGrid className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant={value === 'table' ? 'secondary' : 'ghost'}
        size="sm"
        className={value === 'table' ? 'px-3 shadow-sm' : 'px-3 text-muted-foreground'}
        onClick={() => onChange('table')}
        aria-label={tableLabel}
        title={tableLabel}
      >
        <List className="h-4 w-4" />
      </Button>
    </div>
  );
}

export default function PricingConfigPage() {
  const { user } = useAuth();
  const [pricingTiers, setPricingTiers] = useState([]);
  const [tokenDisplayScale, setTokenDisplayScale] = useState(200);
  const [tokenDisplayScaleDraft, setTokenDisplayScaleDraft] = useState(200);
  const [tokenDisplayScaleSaving, setTokenDisplayScaleSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('plans');
  const [planView, setPlanView] = useState('table');
  const [trialView, setTrialView] = useState('table');
  const [tierEditorView, setTierEditorView] = useState('cards');
  
  // Dialog state
  const [editDialog, setEditDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  
  // Form state for pricing tiers
  const [formData, setFormData] = useState({
    name: '',
    min_users: 1,
    max_users: 249,
    price_per_user: 12,
    description: '',
    employee_limit: 100,
    content_team_limit: 5,
    monthly_token_limit: 2900000,
    conversation_monthly_token_limit: 2500000,
    kb_monthly_token_limit: 400000,
    monthly_cost_limit_usd: 100.0,
    tokens_per_seat_month: 25000,
    user_daily_capacity_pct: 0.0467,
    user_weekly_capacity_pct: 0.3267,
    user_monthly_capacity_pct: 1.40,
    document_limit: 100,
    max_chunks_per_document: 50,
    topics_limit: 30,
    content_gap_limit: 100,
    integrations_limit: 2,
    monthly_export_limit: 5,
    scheduled_reports_limit: 5,
    notification_limit: 10,
    features: [],
    is_active: true,
  });
  const [newFeature, setNewFeature] = useState('');

  // Default trial config state - initialized as null, loaded from API
  const [trialDefaults, setTrialDefaults] = useState(null);
  const [trialLoading, setTrialLoading] = useState(true);
  const [trialError, setTrialError] = useState(null);
  const [trialSaving, setTrialSaving] = useState(false);

  // Model options for dropdowns
  const MODEL_OPTIONS = {
    fast: [
      { value: 'claude-sonnet-4-6', label: 'Eva Smart' },
      { value: 'claude-haiku-4-5-20251001', label: 'Eva Fast' },
    ],
    thinking: [
      { value: 'claude-opus-4-6', label: 'Eva Deep' },
      { value: 'claude-sonnet-4-6', label: 'Eva Smart' },
    ],
  };

  useEffect(() => {
    fetchPricingTiers();
    fetchTrialDefaults();
  }, []);

  const fetchPricingTiers = async () => {
    try {
      const response = await api.get('/superadmin/pricing-tiers');
      setPricingTiers(response.data.tiers || []);
      if (response.data.token_display_scale) {
        setTokenDisplayScale(response.data.token_display_scale);
        setTokenDisplayScaleDraft(response.data.token_display_scale);
      }
    } catch (err) {
      console.error('Failed to fetch pricing tiers:', err);
      toast.error('Failed to load pricing tiers');
    } finally {
      setLoading(false);
    }
  };

  const fetchTrialDefaults = async () => {
    setTrialLoading(true);
    setTrialError(null);
    try {
      const response = await api.get('/superadmin/default-trial-config');
      if (response.data.token_display_scale) {
        setTokenDisplayScale(response.data.token_display_scale);
        setTokenDisplayScaleDraft(response.data.token_display_scale);
      }
      if (response.data.config) {
        // Merge with safe defaults to prevent NaN in inputs if API returns partial data
        setTrialDefaults(withCalculatedLimitValues({
          duration_days: 10, max_documents: 25, max_chunks_per_document: 50,
          monthly_token_limit: 425000, conversation_monthly_token_limit: 375000,
          kb_monthly_token_limit: 50000, monthly_cost_limit_usd: 25.0,
          tokens_per_seat_month: 75000, employee_limit: 5, content_team_limit: 3,
          document_limit: 25, topics_limit: 15, fast_model: 'claude-sonnet-4-6',
          integrations_limit: 2, content_gap_limit: 10,
          monthly_export_limit: 5, scheduled_reports_limit: 1,
          notification_limit: 3,
          // EIP-229: fall back to the seat-derived guardrails (not the old
          // wrong-unit 10/15/20) and default the override sentinel off; the
          // real persisted config below wins via the spread.
          ...calculateUserCapacityGuardrails({ employee_limit: 5 }),
          user_capacity_pct_overridden: false,
          thinking_model: 'claude-opus-4-6',
          ...response.data.config,
        }));
      } else {
        setTrialError('Trial configuration not found in database');
        toast.error('Trial configuration not found. Please configure it.');
      }
    } catch (err) {
      console.error('Failed to fetch trial defaults:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to load trial configuration';
      setTrialError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setTrialLoading(false);
    }
  };

  const updateTrialLimit = (field, rawValue) => {
    if (isCalculatedLimitField(field)) return;
    const nextValue = field.type === 'decimal'
      ? parseFloat(rawValue) || 0
      : parseInt(rawValue) || 0;
    setTrialDefaults((current) => {
      let next = { ...current, [field.key]: nextValue };
      // EIP-229: editing a per-user capacity guardrail marks it as a custom
      // override so the backend persists it instead of re-deriving from seats.
      if (isUserCapacityField(field)) {
        next.user_capacity_pct_overridden = true;
      }
      next = withCalculatedLimitValues(next);
      // In auto mode, keep the guardrails in lockstep with the seat count.
      if (!next.user_capacity_pct_overridden) {
        next = { ...next, ...calculateUserCapacityGuardrails(next) };
      }
      return buildTrialConfigPayload(next);
    });
  };

  // EIP-229: drop the custom guardrails and re-derive them from the seat basis.
  const resetTrialCapacityToAuto = () => {
    setTrialDefaults((current) => buildTrialConfigPayload(withCalculatedLimitValues({
      ...current,
      user_capacity_pct_overridden: false,
      ...calculateUserCapacityGuardrails(current),
    })));
  };

  const updateTierLimit = (field, rawValue) => {
    if (isCalculatedLimitField(field)) return;
    const nextValue = field.type === 'decimal'
      ? parseFloat(rawValue) || 0
      : parseInt(rawValue) || 0;
    setFormData((current) => withCalculatedLimitValues({
      ...current,
      [field.key]: nextValue,
    }));
  };

  const handleSaveTrialDefaults = async () => {
    setTrialSaving(true);
    try {
      const overridden = Boolean(trialDefaults.user_capacity_pct_overridden);
      await api.put('/superadmin/default-trial-config', buildTrialConfigPayload(withCalculatedLimitValues({
        ...trialDefaults,
        // Auto mode: re-derive the guardrails from seats so the saved default
        // tracks the seat basis. Custom mode: keep the operator's typed values.
        ...(overridden ? {} : calculateUserCapacityGuardrails(trialDefaults)),
        user_capacity_pct_overridden: overridden,
      })));
      toast.success('Default trial configuration saved');
    } catch (err) {
      console.error('Failed to save trial defaults:', err);
      toast.error(err.response?.data?.detail || 'Failed to save trial defaults');
    } finally {
      setTrialSaving(false);
    }
  };

  const handleSaveTokenDisplayScale = async () => {
    const nextScale = parseInt(tokenDisplayScaleDraft, 10);
    if (!Number.isFinite(nextScale) || nextScale < 1) {
      toast.error('Credit display scale must be at least 1');
      return;
    }

    setTokenDisplayScaleSaving(true);
    try {
      const response = await api.put('/superadmin/token-display-scale', { scale: nextScale });
      const savedScale = response.data.token_display_scale || nextScale;
      setTokenDisplayScale(savedScale);
      setTokenDisplayScaleDraft(savedScale);
      toast.success('Credit display scale saved');
    } catch (err) {
      console.error('Failed to save token display scale:', err);
      toast.error(err.response?.data?.detail || 'Failed to save credit display scale');
    } finally {
      setTokenDisplayScaleSaving(false);
    }
  };

  const handleEditTier = (tier) => {
    setSelectedTier(tier);
    setFormData(withCalculatedLimitValues({
      name: tier.name,
      min_users: tier.min_users,
      max_users: tier.max_users,
      price_per_user: Math.round(tier.price_per_user),
      description: tier.description || '',
      employee_limit: tier.employee_limit ?? 100,
      content_team_limit: tier.content_team_limit ?? 5,
      monthly_token_limit: tier.monthly_token_limit ?? 2900000,
      conversation_monthly_token_limit: tier.conversation_monthly_token_limit ?? 2500000,
      kb_monthly_token_limit: tier.kb_monthly_token_limit ?? 400000,
      monthly_cost_limit_usd: tier.monthly_cost_limit_usd ?? 100.0,
      tokens_per_seat_month: tier.tokens_per_seat_month ?? 25000,
      user_daily_capacity_pct: tier.user_daily_capacity_pct ?? 0.0467,
      user_weekly_capacity_pct: tier.user_weekly_capacity_pct ?? 0.3267,
      user_monthly_capacity_pct: tier.user_monthly_capacity_pct ?? 1.40,
      document_limit: tier.document_limit ?? 100,
      max_chunks_per_document: tier.max_chunks_per_document ?? 50,
      topics_limit: tier.topics_limit ?? 30,
      content_gap_limit: tier.content_gap_limit ?? 100,
      integrations_limit: tier.integrations_limit ?? 2,
      monthly_export_limit: tier.monthly_export_limit ?? 5,
      scheduled_reports_limit: tier.scheduled_reports_limit ?? 5,
      notification_limit: tier.notification_limit ?? 10,
      features: tier.features || [],
      is_active: tier.is_active === 1 || tier.is_active === true,
    }));
    setNewFeature('');
    setTierEditorView(planView);
    setEditDialog(true);
  };

  const handleAddTier = () => {
    setSelectedTier(null);
    const lastTier = pricingTiers[pricingTiers.length - 1];
    const nextMin = lastTier ? lastTier.max_users + 1 : 1;
    
    setFormData(withCalculatedLimitValues({
      name: '',
      min_users: nextMin,
      max_users: nextMin + 249,
      price_per_user: 10,
      description: '',
      employee_limit: 100,
      content_team_limit: 5,
      monthly_token_limit: 2900000,
      conversation_monthly_token_limit: 2500000,
      kb_monthly_token_limit: 400000,
      monthly_cost_limit_usd: 100.0,
      tokens_per_seat_month: 25000,
      user_daily_capacity_pct: 0.0467,
      user_weekly_capacity_pct: 0.3267,
      user_monthly_capacity_pct: 1.40,
      document_limit: 100,
      max_chunks_per_document: 50,
      topics_limit: 30,
      content_gap_limit: 100,
      integrations_limit: 2,
      monthly_export_limit: 5,
      scheduled_reports_limit: 5,
      notification_limit: 10,
      features: [],
      is_active: true,
    }));
    setNewFeature('');
    setTierEditorView(planView);
    setEditDialog(true);
  };

  const handleAddFeature = () => {
    if (newFeature.trim()) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, newFeature.trim()]
      }));
      setNewFeature('');
    }
  };

  const handleRemoveFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const handleSaveTier = async () => {
    if (!formData.name.trim()) {
      toast.error('Tier name is required');
      return;
    }
    if (formData.min_users > formData.max_users) {
      toast.error('Minimum users cannot exceed maximum users');
      return;
    }
    if (formData.price_per_user < 1) {
      toast.error('Price per user must be at least $1');
      return;
    }

    setSaving(true);
    try {
      const calculatedFormData = withCalculatedLimitValues(formData);
      const tierData = {
        id: selectedTier?.id || null,
        name: calculatedFormData.name.trim(),
        min_users: parseInt(calculatedFormData.min_users),
        max_users: parseInt(calculatedFormData.max_users),
        price_per_user: parseInt(calculatedFormData.price_per_user),
        description: calculatedFormData.description.trim() || null,
        employee_limit: parseInt(calculatedFormData.employee_limit) || 100,
        content_team_limit: parseInt(calculatedFormData.content_team_limit) || 5,
        monthly_token_limit: parseInt(calculatedFormData.monthly_token_limit) || 2900000,
        conversation_monthly_token_limit: parseInt(calculatedFormData.conversation_monthly_token_limit) || 2500000,
        kb_monthly_token_limit: parseInt(calculatedFormData.kb_monthly_token_limit) || 400000,
        monthly_cost_limit_usd: parseFloat(calculatedFormData.monthly_cost_limit_usd) || 100.0,
        tokens_per_seat_month: parseInt(calculatedFormData.tokens_per_seat_month) || 25000,
        user_daily_capacity_pct: parseFloat(calculatedFormData.user_daily_capacity_pct) || 0.0467,
        user_weekly_capacity_pct: parseFloat(calculatedFormData.user_weekly_capacity_pct) || 0.3267,
        user_monthly_capacity_pct: parseFloat(calculatedFormData.user_monthly_capacity_pct) || 1.40,
        document_limit: parseInt(calculatedFormData.document_limit) || 100,
        max_chunks_per_document: parseInt(calculatedFormData.max_chunks_per_document) || 50,
        topics_limit: parseInt(calculatedFormData.topics_limit) || 30,
        content_gap_limit: parseInt(calculatedFormData.content_gap_limit) || 100,
        integrations_limit: parseInt(calculatedFormData.integrations_limit) || 2,
        monthly_export_limit: parseInt(calculatedFormData.monthly_export_limit) || 5,
        scheduled_reports_limit: parseInt(calculatedFormData.scheduled_reports_limit) || 5,
        notification_limit: parseInt(calculatedFormData.notification_limit) || 10,
        features: calculatedFormData.features,
        is_active: calculatedFormData.is_active,
      };

      await api.put('/superadmin/pricing-tiers', {
        tiers: [tierData],
      });

      toast.success(selectedTier ? 'Pricing tier updated' : 'Pricing tier created');
      setEditDialog(false);
      fetchPricingTiers();
    } catch (err) {
      console.error('Failed to save pricing tier:', err);
      toast.error(err.response?.data?.detail || 'Failed to save pricing tier');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTier = async () => {
    if (!selectedTier) return;

    setSaving(true);
    try {
      await api.delete(`/superadmin/pricing-tiers/${selectedTier.id}`);
      toast.success(`Pricing tier "${selectedTier.name}" deactivated`);
      setDeleteDialog(false);
      setSelectedTier(null);
      fetchPricingTiers();
    } catch (err) {
      console.error('Failed to delete pricing tier:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete pricing tier');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (tier) => {
    setSelectedTier(tier);
    setDeleteDialog(true);
  };

  const calculateExample = (userCount) => {
    const tier = pricingTiers.find(t => 
      userCount >= t.min_users && (t.max_users >= userCount || t.max_users === 999999)
    );
    if (!tier) return null;
    const monthly = userCount * Math.round(tier.price_per_user);
    const annual = monthly * 12;
    const annualDiscounted = Math.round(annual * 0.9);
    return { tier: tier.name, monthly, annual, annualDiscounted };
  };

  const renderTokenScaleCard = () => (
    <Card className="border-primary/25 bg-primary/5">
      <CardContent className="grid gap-4 pt-4 lg:grid-cols-[auto_1fr_minmax(280px,auto)] lg:items-center">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
          <Zap className="h-5 w-5 text-primary" />
        </div>
        <div>
          <p className="font-medium">End-user credit display scale</p>
          <p className="text-sm text-muted-foreground">
            Credit-based plan and trial limits are configured in raw LLM tokens. Tenant-facing usage displays scaled credits.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <div className="space-y-1">
            <Label htmlFor="token-display-scale" className="text-xs text-muted-foreground">
              LLM tokens per credit
            </Label>
            <Input
              id="token-display-scale"
              type="number"
              min="1"
              step="1"
              value={tokenDisplayScaleDraft}
              onChange={(event) => setTokenDisplayScaleDraft(event.target.value)}
              data-testid="token-display-scale-input"
            />
          </div>
          <Button
            type="button"
            onClick={handleSaveTokenDisplayScale}
            disabled={tokenDisplayScaleSaving || Number(tokenDisplayScaleDraft) === Number(tokenDisplayScale)}
            data-testid="save-token-display-scale-btn"
          >
            {tokenDisplayScaleSaving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save
          </Button>
          <Badge variant="outline" className="w-fit self-start text-sm sm:self-auto">
            Current: {formatNumber(tokenDisplayScale)}:1
          </Badge>
        </div>
      </CardContent>
    </Card>
  );

  const renderTokenScaleValue = (value, field, variant = 'stacked') => {
    if (!isTokenLimitField(field)) {
      return formatLimitValue(value, field);
    }

    const userTokens = toUserTokens(value, tokenDisplayScale);
    if (variant === 'inline') {
      return `${formatLimitValue(userTokens, field)} credits / ${formatLimitValue(value, field)} LLM`;
    }

    return (
      <div className="space-y-1">
        <div className="font-semibold">{formatLimitValue(userTokens, field)} credits</div>
        <div className="text-xs text-muted-foreground">{formatLimitValue(value, field)} LLM tokens</div>
      </div>
    );
  };

  const renderLimitValueBadge = (value, field) => (
    <Badge variant="outline" className="h-auto justify-start text-left text-xs">
      {renderTokenScaleValue(value, field)}
    </Badge>
  );

  if (loading) {
    return (
      <PageSkeleton maxWidth="7xl" kpis={3} tabs sections={2} data-testid="plans-trials-loading" />
    );
  }

  if (!user?.is_superadmin) {
    return (
      <PageShell maxWidth="7xl" contentClassName="flex min-h-[400px] flex-col items-center justify-center text-center">
        <AlertTriangle className="h-12 w-12 text-destructive" />
        <h2 className="text-xl font-semibold">Access Denied</h2>
        <p className="text-muted-foreground">Only superadmins can access this page.</p>
      </PageShell>
    );
  }

  return (
    <PageShell maxWidth="7xl" data-testid="plans-trials-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Plans & Trials</h1>
        <p className="text-muted-foreground">
          Configure global plan and trial defaults. Tenant Details can override these values per tenant.
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex h-auto flex-wrap gap-1 rounded-lg bg-muted/50 p-1">
          <TabsTrigger value="plans" data-testid="tab-plans">
            <DollarSign className="h-4 w-4 mr-2" />
            Default Plan Limits
          </TabsTrigger>
          <TabsTrigger value="trial-defaults" data-testid="tab-trial-defaults">
            <Clock className="h-4 w-4 mr-2" />
            Default Trial Limits
          </TabsTrigger>
        </TabsList>

        {/* Plans Tab */}
        <TabsContent value="plans" className="space-y-6">
          {renderTokenScaleCard()}

          {/* Info Banner */}
          <Card className="border-primary/30 bg-primary/5">
            <CardContent className="flex items-start gap-3 pt-4">
              <Info className="h-5 w-5 text-primary mt-0.5" />
              <div className="text-sm text-foreground">
                <p className="font-medium">Global plan defaults</p>
                <p className="text-muted-foreground">
                  These values are the baseline for Tenant Details &gt; Plan & Limits. Tenant overrides win when configured.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Pricing Tiers
                </CardTitle>
                <CardDescription>
                  Volume-based pricing tiers with all plan limits
                </CardDescription>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <ViewToggle value={planView} onChange={setPlanView} />
                <Button onClick={handleAddTier} data-testid="add-tier-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Tier
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {planView === 'cards' ? (
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                  {pricingTiers.map((tier) => (
                    <div key={tier.id} className="rounded-lg border border-primary/20 bg-card p-5 shadow-sm">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h3 className="text-lg font-semibold">{formatCellValue(tier.name)}</h3>
                          <p className="text-sm text-muted-foreground">
                            {formatNumber(tier.min_users)} - {tier.max_users >= 999999 ? 'Enterprise' : formatNumber(tier.max_users)} users
                          </p>
                        </div>
                        <Badge variant={tier.is_active ? 'default' : 'secondary'}>{tier.is_active ? 'Active' : 'Inactive'}</Badge>
                      </div>
                      <div className="mt-5">
                        <p className="text-3xl font-bold">
                          {tier.price_per_user === null || tier.price_per_user === undefined ? 'Custom' : formatCurrency(tier.price_per_user)}
                        </p>
                        <p className="text-sm text-muted-foreground">per user/month</p>
                      </div>
                      <div className="mt-5 space-y-4">
                        {LIMIT_SECTIONS.map((section) => (
                          <div key={section.key}>
                            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                              <SectionLabel section={section} compact />
                            </p>
                            <div className="grid grid-cols-2 gap-3">
                              {getLimitFieldsForSection(section).map((field) => (
                                <div key={field.key} className="rounded-md bg-muted/50 p-3">
                                  <p className="text-xs text-muted-foreground">{field.label}</p>
                                  <div className="mt-1">{renderTokenScaleValue(tier[field.key], field)}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                      <Button variant="outline" className="mt-5 w-full" onClick={() => handleEditTier(tier)}>
                        <Pencil className="h-4 w-4 mr-2" />
                        Edit Defaults
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Section</TableHead>
                        <TableHead>Limit</TableHead>
                        <TableHead>Unit</TableHead>
                        {pricingTiers.map((tier) => (
                          <TableHead key={tier.id} className="min-w-[190px]">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <div className="font-semibold text-foreground">{formatCellValue(tier.name)}</div>
                                <div className="text-xs font-normal text-muted-foreground">
                                  {formatNumber(tier.min_users)} - {tier.max_users >= 999999 ? '∞' : formatNumber(tier.max_users)} users
                                </div>
                                <div className="mt-1 text-xs font-semibold text-success">
                                  {tier.price_per_user === null || tier.price_per_user === undefined ? formatCellValue(tier.price_per_user) : `${formatCurrency(tier.price_per_user)}/user`}
                                </div>
                                <Badge variant={tier.is_active ? "default" : "secondary"} className={tier.is_active ? "mt-2 bg-success text-success-foreground" : "mt-2"}>
                                  {tier.is_active ? 'Active' : 'Inactive'}
                                </Badge>
                              </div>
                              <div className="flex shrink-0 items-center gap-1">
                                <Button variant="ghost" size="icon" onClick={() => handleEditTier(tier)} data-testid={`edit-tier-${tier.id}`}>
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive" onClick={() => confirmDelete(tier)} data-testid={`delete-tier-${tier.id}`}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {LIMIT_SECTIONS.map((section) => (
                        getLimitFieldsForSection(section).map((field, index) => {
                          const Icon = field.icon;
                          return (
                            <TableRow key={field.key}>
                              <TableCell className="text-muted-foreground">
                                {index === 0 ? <SectionLabel section={section} compact /> : ''}
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2 font-medium">
                                  <Icon className="h-4 w-4 text-primary" />
                                  {field.label}
                                </div>
                              </TableCell>
                              <TableCell className="text-muted-foreground">{field.unit}</TableCell>
                              {pricingTiers.map((tier) => (
                                <TableCell key={`${tier.id}-${field.key}`}>
                                  {renderLimitValueBadge(tier[field.key], field)}
                                </TableCell>
                              ))}
                            </TableRow>
                          );
                        })
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pricing Calculator Preview */}
          <Card>
            <CardHeader>
              <CardTitle>Pricing Calculator Preview</CardTitle>
              <CardDescription>
                See how pricing would work for different team sizes
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                {[10, 100, 300, 1000].map((count) => {
                  const pricing = calculateExample(count);
                  return (
                    <div key={count} className="p-4 rounded-lg bg-secondary/50 text-center">
                      <div className="flex items-center justify-center gap-1 text-muted-foreground mb-2">
                        <Users className="h-4 w-4" />
                        <span className="text-sm">{formatNumber(count)} users</span>
                      </div>
                      {pricing ? (
                        <>
                          <p className="text-xl font-bold">{formatCurrency(pricing.monthly)}/mo</p>
                          <p className="text-xs text-muted-foreground">{pricing.tier} tier</p>
                          <p className="text-xs text-emerald-600 mt-1">
                            {formatCurrency(pricing.annualDiscounted)}/yr with annual
                          </p>
                        </>
                      ) : (
                        <p className="text-muted-foreground">No tier</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Default Trial Limits Tab */}
        <TabsContent value="trial-defaults" className="space-y-6">
          {renderTokenScaleCard()}

          {/* Info Banner */}
          <Card className="border-warning/30 bg-warning/5">
            <CardContent className="flex items-start gap-3 pt-4">
              <Info className="h-5 w-5 text-warning mt-0.5" />
              <div className="text-sm text-foreground">
                <p className="font-medium">Default trial limits</p>
                <p className="text-muted-foreground">
                  These values seed new trial tenants. Tenant Details &gt; Plan & Limits can customize a specific tenant trial.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Default Trial Limits
                </CardTitle>
                <CardDescription>
                  Configure the default limits for trial tenants. These values pre-fill when using "Configure Trial" action.
                </CardDescription>
              </div>
              <ViewToggle value={trialView} onChange={setTrialView} />
            </CardHeader>
            <CardContent className="space-y-6">
              {trialLoading ? (
                <div className="space-y-3" data-testid="trial-defaults-loading">
                  <Skeleton className="h-20 w-full rounded-xl" />
                  <div className="grid gap-3 md:grid-cols-2">
                    <Skeleton className="h-24 w-full rounded-xl" />
                    <Skeleton className="h-24 w-full rounded-xl" />
                  </div>
                </div>
              ) : trialError ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <AlertTriangle className="h-12 w-12 text-destructive mb-4" />
                  <p className="text-destructive font-medium mb-2">Failed to load trial configuration</p>
                  <p className="text-sm text-muted-foreground mb-4">{trialError}</p>
                  <Button onClick={fetchTrialDefaults} variant="outline">
                    Retry
                  </Button>
                </div>
              ) : !trialDefaults ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <AlertTriangle className="h-12 w-12 text-amber-500 mb-4" />
                  <p className="font-medium mb-2">Trial configuration not initialized</p>
                  <p className="text-sm text-muted-foreground">Please run database initialization or contact support.</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 gap-4 border-t pt-4 md:grid-cols-3">
                    <div className="space-y-2">
                      <Label htmlFor="duration_days">Duration (days)</Label>
                      <Input
                        id="duration_days"
                        type="number"
                        min="1"
                        value={trialDefaults.duration_days}
                        onChange={(e) => setTrialDefaults({ ...trialDefaults, duration_days: parseInt(e.target.value) || 1 })}
                        data-testid="trial-duration-input"
                      />
                    </div>
                  </div>

                  {trialView === 'cards' ? (
                    <div className="space-y-5 border-t pt-4">
                      {LIMIT_SECTIONS.map((section) => (
                        <div key={section.key} className="rounded-lg border bg-card p-4">
                          <h4 className="mb-4 text-sm font-semibold"><SectionLabel section={section} /></h4>
                          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                            {getLimitFieldsForSection(section).map((field) => {
                              const Icon = field.icon;
                              return (
                                <div key={field.key} className="space-y-2 rounded-lg bg-muted/40 p-3">
                                  <div className="flex items-center gap-2">
                                    <Icon className="h-4 w-4 text-primary" />
                                    <Label htmlFor={`trial-${field.key}`}>{field.label}</Label>
                                  </div>
                                  {isReadOnlyTrialField(field) ? (
                                    <div
                                      className="rounded-md border border-dashed bg-background px-3 py-2"
                                      data-testid={`trial-limit-readonly-${field.key}`}
                                    >
                                      <div className="flex items-center justify-between gap-2">
                                        <span className="font-medium">{formatLimitValue(getTrialReadonlyValue(field, trialDefaults), field)}</span>
                                        <Badge variant="secondary">Calculated</Badge>
                                      </div>
                                    </div>
                                  ) : (
                                    <Input
                                      id={`trial-${field.key}`}
                                      data-testid={`trial-limit-input-${field.key}`}
                                      type="number"
                                      min="0"
                                      step={field.type === 'decimal' ? '0.01' : '1'}
                                      value={trialDefaults[field.key]}
                                      onChange={(e) => updateTrialLimit(field, e.target.value)}
                                    />
                                  )}
                                  {isTokenLimitField(field) ? (
                                    <p className="text-xs text-muted-foreground">
                                      {formatLimitValue(toUserTokens(getLimitValue(field, trialDefaults), tokenDisplayScale), field)} credits · {formatLimitValue(getLimitValue(field, trialDefaults), field)} LLM tokens
                                    </p>
                                  ) : isUserCapacityField(field) ? (
                                    <p className="text-xs text-muted-foreground">
                                      {trialDefaults.user_capacity_pct_overridden ? 'Custom value' : 'Auto-scaled to employee seats · edit to customize'}
                                    </p>
                                  ) : (
                                    <p className="text-xs text-muted-foreground">{field.unit}</p>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="overflow-x-auto rounded-lg border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Section</TableHead>
                            <TableHead>Limit</TableHead>
                            <TableHead>Unit</TableHead>
                            <TableHead>Credits</TableHead>
                            <TableHead>LLM Tokens</TableHead>
                            <TableHead className="w-[220px]">Default (LLM)</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {LIMIT_SECTIONS.map((section) => (
                            getLimitFieldsForSection(section).map((field, index) => {
                              const Icon = field.icon;
                              return (
                                <TableRow key={field.key}>
                                  <TableCell className="text-muted-foreground">
                                    {index === 0 ? <SectionLabel section={section} compact /> : ''}
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-2 font-medium">
                                      <Icon className="h-4 w-4 text-primary" />
                                      {field.label}
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-muted-foreground">{field.unit}</TableCell>
                                  <TableCell className="font-medium">
                                    {isTokenLimitField(field)
                                      ? formatLimitValue(toUserTokens(getLimitValue(field, trialDefaults), tokenDisplayScale), field)
                                      : '—'}
                                  </TableCell>
                                  <TableCell className="text-muted-foreground">
                                    {isTokenLimitField(field)
                                      ? formatLimitValue(getLimitValue(field, trialDefaults), field)
                                      : '—'}
                                  </TableCell>
                                  <TableCell>
                                    {isReadOnlyTrialField(field) ? (
                                      <div
                                        className="flex items-center justify-between gap-2 rounded-md border border-dashed bg-muted/40 px-3 py-2"
                                        data-testid={`trial-limit-readonly-${field.key}`}
                                      >
                                        <span className="font-medium">{formatLimitValue(getTrialReadonlyValue(field, trialDefaults), field)}</span>
                                        <Badge variant="secondary">Calculated</Badge>
                                      </div>
                                    ) : (
                                      <Input
                                        data-testid={`trial-limit-input-${field.key}`}
                                        type="number"
                                        min="0"
                                        step={field.type === 'decimal' ? '0.01' : '1'}
                                        value={trialDefaults[field.key]}
                                        onChange={(e) => updateTrialLimit(field, e.target.value)}
                                      />
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}

                  {trialDefaults.user_capacity_pct_overridden ? (
                    <div
                      className="mt-4 flex items-center justify-between gap-3 rounded-lg border border-dashed bg-muted/40 px-3 py-2 text-xs text-muted-foreground"
                      data-testid="trial-capacity-override-notice"
                    >
                      <span>
                        Per-user rate limit guardrails are set to custom values and will not auto-scale with the seat count.
                      </span>
                      <Button type="button" variant="outline" size="sm" onClick={resetTrialCapacityToAuto}>
                        Reset to auto
                      </Button>
                    </div>
                  ) : null}

                  {/* Eva Models */}
                  <div className="border-t pt-4">
                    <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                      <Zap className="h-4 w-4 text-purple-500" />
                      Eva Model Configuration
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Standard Model</Label>
                        <select
                          value={trialDefaults.fast_model}
                          onChange={(e) => setTrialDefaults({ ...trialDefaults, fast_model: e.target.value })}
                          className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                          data-testid="trial-fast-model-select"
                        >
                          {MODEL_OPTIONS.fast.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                        <p className="text-xs text-muted-foreground">Used for quick responses</p>
                      </div>
                      <div className="space-y-2">
                        <Label>Advanced Model</Label>
                        <select
                          value={trialDefaults.thinking_model}
                          onChange={(e) => setTrialDefaults({ ...trialDefaults, thinking_model: e.target.value })}
                          className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                          data-testid="trial-thinking-model-select"
                        >
                          {MODEL_OPTIONS.thinking.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                        <p className="text-xs text-muted-foreground">Used for complex reasoning</p>
                      </div>
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="flex justify-end pt-4">
                    <Button onClick={handleSaveTrialDefaults} disabled={trialSaving} data-testid="save-trial-defaults-btn">
                      {trialSaving ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4 mr-2" />
                      )}
                      Save Default Trial Config
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit/Add Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>
              {selectedTier ? 'Edit Pricing Tier' : 'Add Pricing Tier'}
            </DialogTitle>
            <DialogDescription>
              Configure the pricing tier details. Changes apply to all tenants.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Tier Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Starter, Growth, Enterprise"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="min_users">Minimum Users</Label>
                <Input
                  id="min_users"
                  type="number"
                  min="1"
                  value={formData.min_users}
                  onChange={(e) => setFormData({ ...formData, min_users: parseInt(e.target.value) || 1 })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="max_users">Maximum Users</Label>
                <Input
                  id="max_users"
                  type="number"
                  min="1"
                  value={formData.max_users}
                  onChange={(e) => setFormData({ ...formData, max_users: parseInt(e.target.value) || 1 })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="price_per_user">Price per User ($/month)</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="price_per_user"
                  type="number"
                  min="1"
                  value={formData.price_per_user}
                  onChange={(e) => setFormData({ ...formData, price_per_user: parseInt(e.target.value) || 1 })}
                  className="pl-9"
                />
              </div>
              <p className="text-xs text-muted-foreground">Whole dollars only, no decimals</p>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <Label>Plan Limits</Label>
                  <p className="text-xs text-muted-foreground">Use -1 only when a limit is explicitly unlimited.</p>
                </div>
                <ViewToggle value={tierEditorView} onChange={setTierEditorView} />
              </div>
              {tierEditorView === 'cards' ? (
                <div className="space-y-4">
                  {LIMIT_SECTIONS.map((section) => (
                    <div key={section.key} className="rounded-lg border bg-card p-4">
                      <h4 className="mb-4 text-sm font-semibold"><SectionLabel section={section} /></h4>
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {getLimitFieldsForSection(section).map((field) => (
                          <div key={field.key} className="space-y-2">
                            <Label htmlFor={`tier-${field.key}`}>{field.label}</Label>
                            {isCalculatedLimitField(field) ? (
                              <div className="rounded-md border border-dashed bg-background px-3 py-2">
                                <div className="flex items-center justify-between gap-2">
                                  <span className="font-medium">{formatLimitValue(getLimitValue(field, formData), field)}</span>
                                  <Badge variant="secondary">Calculated</Badge>
                                </div>
                              </div>
                            ) : (
                              <Input
                                id={`tier-${field.key}`}
                                type="number"
                                min="-1"
                                step={field.type === 'decimal' ? '0.01' : '1'}
                                value={formData[field.key]}
                                onChange={(e) => updateTierLimit(field, e.target.value)}
                              />
                            )}
                            <p className="text-xs text-muted-foreground">
                              {isTokenLimitField(field)
                                ? `${formatLimitValue(toUserTokens(getLimitValue(field, formData), tokenDisplayScale), field)} credits · ${formatLimitValue(getLimitValue(field, formData), field)} LLM tokens`
                                : `${field.unit} · -1 = unlimited`}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="overflow-hidden rounded-lg border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Section</TableHead>
                        <TableHead>Limit</TableHead>
                        <TableHead>Unit</TableHead>
                        <TableHead>Credits</TableHead>
                        <TableHead>LLM Tokens</TableHead>
                        <TableHead className="w-[220px]">Value (LLM)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {LIMIT_SECTIONS.map((section) => (
                        getLimitFieldsForSection(section).map((field, index) => (
                          <TableRow key={field.key}>
                            <TableCell className="text-muted-foreground">
                              {index === 0 ? <SectionLabel section={section} compact /> : ''}
                            </TableCell>
                            <TableCell className="font-medium">{field.label}</TableCell>
                            <TableCell className="text-muted-foreground">{field.unit}</TableCell>
                            <TableCell className="font-medium">
                              {isTokenLimitField(field)
                                ? formatLimitValue(toUserTokens(getLimitValue(field, formData), tokenDisplayScale), field)
                                : '—'}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {isTokenLimitField(field)
                                ? formatLimitValue(getLimitValue(field, formData), field)
                                : '—'}
                            </TableCell>
                            <TableCell>
                              {isCalculatedLimitField(field) ? (
                                <div className="flex items-center justify-between gap-2 rounded-md border border-dashed bg-muted/40 px-3 py-2">
                                  <span className="font-medium">{formatLimitValue(getLimitValue(field, formData), field)}</span>
                                  <Badge variant="secondary">Calculated</Badge>
                                </div>
                              ) : (
                                <Input
                                  type="number"
                                  min="-1"
                                  step={field.type === 'decimal' ? '0.01' : '1'}
                                  value={formData[field.key]}
                                  onChange={(e) => updateTierLimit(field, e.target.value)}
                                />
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description (optional)</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="e.g., For small teams getting started"
              />
            </div>

            {/* Features */}
            <div className="space-y-2">
              <Label>Features</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Add a feature..."
                  value={newFeature}
                  onChange={(e) => setNewFeature(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddFeature())}
                />
                <Button type="button" variant="outline" size="sm" onClick={handleAddFeature}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {formData.features.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.features.map((feature, idx) => (
                    <Badge key={idx} variant="secondary" className="pr-1">
                      {feature}
                      <button
                        type="button"
                        onClick={() => handleRemoveFeature(idx)}
                        className="ml-1 hover:text-destructive"
                      >
                        ×
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Active</Label>
                <p className="text-xs text-muted-foreground">Inactive tiers won&apos;t be available</p>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>

            {/* Preview */}
            <div className="p-4 rounded-lg bg-secondary/50 mt-4">
              <h4 className="text-sm font-medium mb-2">Preview</h4>
              <p className="text-sm text-muted-foreground">
                {formatNumber(formData.min_users)} - {formatNumber(formData.max_users)} users: 
                <span className="font-semibold text-foreground ml-1">
                  {formatCurrency(formData.price_per_user)}/user/month
                </span>
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Example: 100 users = {formatCurrency(100 * formData.price_per_user)}/month 
                ({formatCurrency(Math.round(100 * formData.price_per_user * 12 * 0.9))}/year with 10% annual discount)
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveTier} disabled={saving}>
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {selectedTier ? 'Save Changes' : 'Create Tier'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate Pricing Tier?</AlertDialogTitle>
            <AlertDialogDescription>
              This will deactivate the &quot;{selectedTier?.name}&quot; tier. Existing subscriptions 
              won&apos;t be affected, but new subscriptions won&apos;t be able to use this tier.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTier}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageShell>
  );
}
